/**
 * 환경변수 (Vite import.meta.env) wrapper + Zod 검증.
 * 누락 / 형식 오류 시 startup 시점에 throw.
 *
 * 디버그 메뉴에서 런타임에 백엔드 prefix 를 override 할 수 있다.
 * - localStorage `pkr-debug-api-base` 에 저장된 값이 있으면 우선 사용
 * - 없으면 build-time `VITE_API_BASE_URL` 사용
 * - axios `client` 의 request interceptor 에서 매 요청 시 평가됨
 */

import { z } from "zod";

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default("/api/v1"),
  VITE_APP_NAME: z.string().default("Duckflex Template"),
  VITE_APP_DEEP_LINK_SCHEME: z.string().default("peeka"),
  VITE_ENABLE_LOGGING: z.coerce.boolean().default(false),
  VITE_GOOGLE_CLIENT_ID: z.string().optional().default(""),
  VITE_GOOGLE_REDIRECT_URI: z.string().optional().default(""),
  /** Apple Services ID (웹/Android client_id). 미설정 시 웹 Apple 로그인 비활성. */
  VITE_APPLE_CLIENT_ID: z.string().optional().default(""),
  /** Apple 웹 redirect_uri = BE web-callback (FE 페이지 아님). Apple 이 form_post POST → BE relay. */
  VITE_APPLE_REDIRECT_URI: z.string().optional().default(""),
  /** OTA 채널 — internal | beta | production. version-policy 조회 시 BE 로 전달. */
  VITE_OTA_CHANNEL: z.string().optional().default("internal"),
  /** App Store 숫자 ID (예: 6451234567). 미설정 시 iOS 스토어 이동 버튼/링크 비활성. */
  VITE_APP_STORE_ID: z.string().optional().default(""),
  /**
   * Play Store 패키지 ID. 스토어 정식 공개 후 설정 (예: kr.duckflex.peeka.app).
   * 미설정 시 웹(ComingSoonPage) 스토어 버튼은 숨김. 앱 내부 OTA 스토어 게이트는
   * 네이티브 설치본이 전제라 기본 패키지로 fallback (liveUpdate.ts).
   */
  VITE_PLAY_STORE_PACKAGE: z.string().optional().default(""),
  /**
   * Firebase 웹앱 설정 4종 — **웹**에서 GA4(Firebase Analytics) JS SDK 초기화용.
   * peeka 의 웹은 부수적인 게 아니다: 앱 호스트(peeka.app)와 앱 안내/약관 호스트
   * (app.peeka.app) 가 둘 다 이 설정으로 돈다.
   *
   * ⚠ 4개가 **모두** 있어야 켜진다 — 하나라도 비면 initAnalytics 가 disabled 로 빠져
   *   웹 수집이 통째로 조용히 죽는다(에러도 안 난다). 지금 세 env 파일 전부 빈 값이다.
   * 네이티브 앱은 google-services.json / GoogleService-Info.plist 로 자동 초기화라 무관.
   *
   * 공개 가능한 값이다(비밀 아님 — .env.* 커밋 정책과 같다). 다만 환경별로 Firebase
   * 앱을 따로 등록하면 값이 달라지므로 .env.dev / .env.prod 에 각각 넣는다.
   * 절차: duckflex-peeka-docs/output_docs/50_GUIDES/ANALYTICS_GA4.md §5
   */
  VITE_FIREBASE_API_KEY: z.string().optional().default(""),
  VITE_FIREBASE_PROJECT_ID: z.string().optional().default(""),
  VITE_FIREBASE_APP_ID: z.string().optional().default(""),
  VITE_FIREBASE_MEASUREMENT_ID: z.string().optional().default(""),
  /**
   * 로컬 dev PC 의 LAN host (IP). git-ignored .env.local 에서만 주입 (개인 머신마다 다름).
   * 디버그 메뉴 "Local (PC IP)" preset (http://<host>:8081/api/v1) 에 사용.
   * 미설정 시 해당 preset 은 숨김. 소스에 IP 를 박지 않기 위함 (.env.local.example 참고).
   */
  VITE_DEV_PC_HOST: z.string().optional().default(""),
  /**
   * 카드 이미지 base URL — 카탈로그의 IMG_PATHWY 는 상대 키(`card/{serlno}/front-{hash}.jpg`)
   * 로 저장되므로 앞에 붙일 오리진이 필요하다 (adbe CardImageCopyService.buildRelativePath).
   * 미설정이면 이미지를 표시하지 않는다(플레이스홀더). 값이 이미 절대 URL 이면 그대로 쓴다.
   */
  VITE_CARD_IMAGE_BASE_URL: z.string().optional().default(""),
  /**
   * 환율 파일(S3/CloudFront) 오리진. `<base>/fx/latest.json` 을 앱이 **직접** 읽는다.
   *
   * BE 를 거치지 않는 이유(2026-08-24 결정): 전 사용자가 부팅마다 읽는데 내용은 하루 한 번만
   * 바뀐다 — 서버를 태우면 그 트래픽이 전부 오리진으로 오고, 글로벌로 나가면 왕복 지연까지
   * 붙는다. 카드 이미지와 같은 버킷·같은 공개 프리픽스라 값은 보통
   * `VITE_CARD_IMAGE_BASE_URL` 과 같다.
   *
   * 미설정이면 표시 통화 전환이 꺼진다(KRW 고정) — 환율 없이 환산하지 않는다.
   */
  VITE_FX_BASE_URL: z.string().optional().default(""),
});

export type AppEnv = z.infer<typeof envSchema>;

function loadEnv(): AppEnv {
  const raw = {
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
    VITE_APP_DEEP_LINK_SCHEME: import.meta.env.VITE_APP_DEEP_LINK_SCHEME,
    VITE_ENABLE_LOGGING: import.meta.env.VITE_ENABLE_LOGGING,
    VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    VITE_GOOGLE_REDIRECT_URI: import.meta.env.VITE_GOOGLE_REDIRECT_URI,
    VITE_APPLE_CLIENT_ID: import.meta.env.VITE_APPLE_CLIENT_ID,
    VITE_APPLE_REDIRECT_URI: import.meta.env.VITE_APPLE_REDIRECT_URI,
    VITE_OTA_CHANNEL: import.meta.env.VITE_OTA_CHANNEL,
    VITE_APP_STORE_ID: import.meta.env.VITE_APP_STORE_ID,
    VITE_PLAY_STORE_PACKAGE: import.meta.env.VITE_PLAY_STORE_PACKAGE,
    VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
    VITE_FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    VITE_DEV_PC_HOST: import.meta.env.VITE_DEV_PC_HOST,
    VITE_CARD_IMAGE_BASE_URL: import.meta.env.VITE_CARD_IMAGE_BASE_URL,
    VITE_FX_BASE_URL: import.meta.env.VITE_FX_BASE_URL,
  };
  const parsed = envSchema.safeParse(raw);
  if (!parsed.success) {
    const detail = parsed.error.issues.map(i => `${i.path.join(".")}: ${i.message}`).join("\n");
    throw new Error(`Invalid env config:\n${detail}`);
  }
  return parsed.data;
}

export const env: AppEnv = loadEnv();

/** Vite mode: 'development' | 'production' (본질적으로 build vs dev) */
export const VITE_MODE = import.meta.env.MODE;

/** Vite-injected DEV flag — prod 번들에서는 false 로 트리쉐이킹됨 */
export const IS_DEV = import.meta.env.DEV;

// === Runtime API base override (디버그 메뉴 전용) ===

const API_BASE_OVERRIDE_KEY = "pkr-debug-api-base";

/**
 * API 주소 override 를 허용하는 빌드인가 — **운영 빌드(`--mode prod`)는 허용하지 않는다.**
 *
 * 디버그용 서버 전환은 로그인 화면 워드마크 10탭 → 잠금 해제 창 제목 10탭으로 열리는데,
 * 그 패널은 토큰 검증 **앞** 단계라(검증 서버가 죽었을 때 빠져나오려고) 토큰 없이도 열렸다.
 * 거기서 임의 URL 을 넣으면 모든 API 요청이 — 로그인 토큰 포함 — 그 주소로 갔다(2026-09-11
 * 발견). 운영에서는 "탭 N번 하고 이 주소 넣으세요" 식 토큰 탈취 경로가 된다.
 * 함수로 두는 이유: import.meta.env.MODE 를 호출 시점에 읽어야 테스트가 모드를 바꿀 수 있다.
 */
export function isApiOverrideAllowed(): boolean {
  return import.meta.env.MODE !== "prod";
}

/** localStorage 의 override 값 (없으면 null). 운영 빌드는 저장값이 있어도 무시한다. */
export function getApiBaseOverride(): string | null {
  if (!isApiOverrideAllowed()) return null;
  if (typeof localStorage === "undefined") return null;
  const v = localStorage.getItem(API_BASE_OVERRIDE_KEY);
  return v && v.length > 0 ? v : null;
}

/** override 설정. null/빈문자면 제거 → env 값으로 복귀. 운영 빌드는 저장하지 않는다. */
export function setApiBaseOverride(url: string | null): void {
  if (!isApiOverrideAllowed()) return;
  if (typeof localStorage === "undefined") return;
  if (!url || url.trim().length === 0) {
    localStorage.removeItem(API_BASE_OVERRIDE_KEY);
    return;
  }
  localStorage.setItem(API_BASE_OVERRIDE_KEY, url.trim());
}

/** 현재 effective API base — override 있으면 그것, 없으면 env. */
export function getEffectiveApiBase(): string {
  return getApiBaseOverride() ?? env.VITE_API_BASE_URL;
}

/** 빠른 전환용 preset. local / dev 두 개 + custom. */
export interface ApiBasePreset {
  label: string;
  url: string;
}

// "Local (PC IP)" 는 개인 머신 IP 라 소스에 박지 않고 .env.local 의 VITE_DEV_PC_HOST 로 주입.
// 미설정 시 preset 목록에서 제외 (디버그 메뉴에 노출 안 됨).
const devPcHost = env.VITE_DEV_PC_HOST.trim();
const devPcPreset: readonly ApiBasePreset[] = devPcHost
  ? [{ label: "Local (PC IP)", url: `http://${devPcHost}:8081/api/v1` }]
  : [];

export const API_BASE_PRESETS: readonly ApiBasePreset[] = [
  { label: "Local (Vite proxy)", url: "/api/v1" },
  { label: "Local (에뮬 10.0.2.2)", url: "http://10.0.2.2:8081/api/v1" },
  ...devPcPreset,
  { label: "Dev (AWS)", url: "https://api.dev.peeka.app/api/v1" },
  { label: "Dev (meddogi, 로컬 중계)", url: "https://peeka-api.meddogi.click/api/v1" },
];
